import { nsContract } from '@/app/i18n/namespaces';
import { CONTRACTS } from '../../api/apiUrls';
import Entity from '@/common-utils/components/renders/entity/Entity';
import genUID from '@/common-utils/functions/generateUniqueId';

const ContractsImportProps = {
    apiUrl: CONTRACTS.FILE_ACTION.url,
    namespace: nsContract,
    fileExampleProperties: {
        name: 'add_contracts_example.csv',
        path: '/static/resource/files/add_contracts_example.csv'
    },
    uploadProperties: {
        fileExtAccepted: ['.csv'],
        typeOfUpload: 'text',
        invalidFileEntity: 'modal.import.error.invalid_file'
    },
    countLines: (fileData) => {
        let numberOfContractsIgnored = 0;
        let previousContractNumber = null;
        let previousContractApplication = null;
        const fileLines = fileData.split('\n');
        let numberOfContracts = 0;
        if (fileLines[0].includes('contrat;application;bank_code;rank;contract_label')) {
            // At least 1 since there are headers
            numberOfContractsIgnored = 1;
        }
        for (const line of fileLines) {
            // Don't take in count lines with the same contract number and the same application
            const [currentContractNumber, currentContractApplication] = line.split(';');
            if ((currentContractNumber !== '' && currentContractNumber !== previousContractNumber)
                || (currentContractApplication !== '' && currentContractApplication !== previousContractApplication)) {
                numberOfContracts = numberOfContracts + 1;
            }
            previousContractNumber = currentContractNumber;
            previousContractApplication = currentContractApplication;
        }
        numberOfContracts = numberOfContracts - numberOfContractsIgnored;
        return { numberOfLines: numberOfContracts, file: fileData };
    },
    explanations: () => {
        return (<>
            <div>
                <Entity entity='modal.import.explanations.header' namespace={nsContract} />
            </div>
            <ul className='list-disc list-outside [&_ul]:list-[revert] ml-8'>
                <li><Entity entity='modal.import.explanations.contract' namespace={nsContract} /></li>
                <li><Entity entity='modal.import.explanations.application' namespace={nsContract} /></li>
                <li><Entity entity='modal.import.explanations.bank_code' namespace={nsContract} /></li>
                <li><Entity entity='modal.import.explanations.rank' namespace={nsContract} /></li>
                <li><Entity entity='modal.import.explanations.contract_label' namespace={nsContract} /></li>
            </ul>
            <div className='mt-4'>
                <Entity entity='modal.import.explanations.warning' namespace={nsContract} />
            </div>

        </>);
    },
    confirmation: (numberOfLines) => {
        return <Entity
            entity='modal.import.confirmation'
            namespace={nsContract}
            data={{ numberOfContracts: numberOfLines }}
        />;
    },
    isFileOnError: (fileDetails) => {
        return fileDetails?.errorsReport;
    },
    renderPostImportErrors: (fileDetails, error) => {
        const isCsvUploadOnError = error?.response?.data;
        if (isCsvUploadOnError) {
            const errorCode = error.response.data.errorCode;
            if (errorCode === 'BAD_FILE_FORMAT_EXCEPTION') {
                return (
                    <div>
                        <Entity entity='modal.import.error.BAD_FILE_FORMAT_EXCEPTION' namespace={nsContract} />
                        <br />
                        <br />
                        <Entity entity='modal.import.error.get_example_file' namespace={nsContract} />
                    </div>
                );
            }
            if (errorCode === 'TOO_MANY_RESOURCES') {
                return (
                    <Entity entity='modal.import.error.TOO_MANY_RESOURCES' namespace={nsContract} />
                );
            }
            return (
                <Entity entity='modal.import.error.UNEXPECTED_ERROR' namespace={nsContract} />
            );
        }

        const isCsvContentOnError = fileDetails?.errorsReport;
        if (isCsvContentOnError) {
            // bank code: numbers and letters only - 36 char max
            // label: all characters are authorized - 100 char max (except for ALMA where only numbers and letters are allowed - 12 char max)
            // conctract number: numbers only - 36 char max
            // ranks: numbers only - 255 char max
            const errorsReport = fileDetails.errorsReport;
            const renderErrors = [];
            Object.entries(errorsReport).forEach(errKeyAndValue => {
                // initialization
                const errorFound = errKeyAndValue[0];
                const contractsOnError = errKeyAndValue[1];

                // process
                for (const contract of contractsOnError) {
                    if (!renderErrors.find(x => x.key === contract)) {
                        // create first instance of (contract, errors)
                        renderErrors.push({ key: contract, errors: [errorFound] });
                    } else {
                        // for a given contract, fill the errorFound in its errors array
                        for (const errorObj of renderErrors) {
                            if (errorObj.key === contract) {
                                // if the error isn't already stored, store it
                                if (!errorObj.errors.find(y => y === errorFound)) {
                                    errorObj.errors.push(errorFound);
                                }
                            }
                        }
                    }
                }
            });

            // Append a unique id to each error element using genUID
            const renderErrorsArray = Object.entries(renderErrors.map((item) => ({ ...item, id: genUID() })));

            return (
                <div>
                    <Entity entity='modal.import.error.header' namespace={nsContract} />
                    <br />
                    <br />
                    {
                        renderErrorsArray.length !== 0 ?
                            renderErrorsArray.map((value) => {
                                const contractObj = value[1];
                                return (
                                    <div key={value.id}>
                                        <div>{contractObj.key}</div>
                                        <ul className='list-disc list-outside [&_ul]:list-[revert] ml-8'>
                                            {
                                                contractObj.errors.map((err, errorKey) => {
                                                    return (
                                                        <li key={`${value.id}-${errorKey}`}>
                                                            <Entity entity={`modal.import.error.${err}`} namespace={nsContract} />
                                                        </li>
                                                    );
                                                })
                                            }
                                        </ul>
                                    </div>
                                );
                            })
                            : false
                    }
                    <br />
                    <Entity entity='modal.import.error.footer' namespace={nsContract} />
                </div>
            );
        }
    }
};

export default ContractsImportProps;